import { Routes } from '@angular/router';
import { CreateDemandComponent } from './demand/create/create-demand.component';
import { View } from '@lib/core';
import { DemandContentComponent } from './demand/display/content/demand-content.component';
import { DemandAddProductComponent } from './demand/display/content/add-product/demand-add-product.component';
import { DemandEditProductComponent } from './demand/display/content/edit-product/demand-edit-product.component';
import { PurchaseRequisitionListComponent } from './purchase-requisition/purchase-requisition-list/purchase-requisition-list.component';
import { PurchaseRequisitionDetailComponent } from './purchase-requisition/purchase-requisition-detail/purchase-requisition-detail.component';
import { PostAnnouncePurchaseRequisitionTaskComponent } from './workflow/post-announce-purchase-requisition-task/post-announce-purchase-requisition-task.component';
import { PurchaseRequisitionActiveGuard } from './purchase-requisition/active/purchase-requisition-active.guard';
import { AclAction, AclEvaluationType, AclGuard, AclResource, createAclPermissionRequest } from '@lib/acl';
import { RFQ_TO_DEMAND_PORT } from '@lib/rfq-detail';
import { RfqToDemandAdapter } from './demand/rfq-to-demand.adapter';

// TODO: Lazy loading is impossible to implement since we have two modules importing each other
//       (DemandModule and PurchaseRequisitionModule). We should create separate libs for each module.
export const RFQ_ROUTES: Routes = [
    {
        path: 'list',
        canActivate: [AclGuard],
        data: {
            permission: createAclPermissionRequest(AclResource.rfqList, AclAction.SHOW),
            redirect: `${View.CLIENT}/dashboard`,
        },
        loadChildren: () => import('@lib/rfq-list/feature').then((m) => m.RfqListFeatureModule),
    },
    {
        path: ':id/detail',
        providers: [{ provide: RFQ_TO_DEMAND_PORT, useClass: RfqToDemandAdapter }],
        loadComponent: () => import('@lib/rfq-detail').then((m) => m.RfqDetailComponent),
    },
    {
        // Manual quotation entry needs the dedicated right quotation:edit (EPROC-11829) on top of the
        // base right quotation:show: the form shows the very quotation data quotation:show gates (AK1 of
        // EPROC-11678). All handlers of ManualQuotationController enforce the same two rights server-side.
        //
        // The position above ':id/quotation' is cosmetic, not load-bearing: ':id/quotation' declares
        // loadComponent without children, so Angular only matches it when the URL is fully consumed
        // — '5/quotation/edit' leaves a segment over and is never matched by it, whatever the order.
        path: ':id/quotation/edit',
        canActivate: [AclGuard],
        data: {
            permission: [
                createAclPermissionRequest(AclResource.quotation, AclAction.SHOW),
                createAclPermissionRequest(AclResource.quotation, AclAction.EDIT),
            ],
            permissionEvaluation: AclEvaluationType.AND,
            redirect: `${View.CLIENT}/dashboard`,
        },
        loadComponent: () => import('@lib/rfq-detail').then((m) => m.ManualQuotationComponent),
        canDeactivate: [(component: { canDeactivate: () => boolean | Promise<boolean> }) => component.canDeactivate()],
    },
    {
        path: ':id/quotation',
        canActivate: [AclGuard],
        data: {
            permission: createAclPermissionRequest(AclResource.quotation, AclAction.SHOW),
            redirect: `${View.CLIENT}/dashboard`,
        },
        providers: [{ provide: RFQ_TO_DEMAND_PORT, useClass: RfqToDemandAdapter }],
        loadComponent: () => import('@lib/rfq-detail').then((m) => m.QuotationDetailComponent),
    },
    {
        // Sole entry point of the AI comparison: the quotation detail page and the compare dialog
        // navigate here within the running session, without an OTP deeplink. The AI endpoint requires
        // quotation.comparison:show as the base right and additionally quotation.ai_comparison:show;
        // the AI right never replaces the normal comparison right.
        path: 'quotation/:rfqGrpId/compare',
        canActivate: [AclGuard],
        data: {
            permission: [
                createAclPermissionRequest(AclResource.quotationComparison, AclAction.SHOW),
                createAclPermissionRequest(AclResource.quotationAiComparison, AclAction.SHOW),
            ],
            permissionEvaluation: AclEvaluationType.AND,
            redirect: `${View.CLIENT}/dashboard`,
        },
        loadComponent: () => import('@lib/rfq-detail').then((m) => m.QuotationAiCompareDeeplinkComponent),
    },
    {
        path: 'demand/create',
        canActivate: [PurchaseRequisitionActiveGuard],
        component: CreateDemandComponent,
    },
    {
        path: 'demand/:demandId/add-product',
        canActivate: [PurchaseRequisitionActiveGuard],
        component: DemandAddProductComponent,
    },
    {
        path: 'demand/:demandId/edit/:productId',
        canActivate: [PurchaseRequisitionActiveGuard],
        component: DemandEditProductComponent,
    },
    {
        path: 'demand/:demandId',
        component: DemandContentComponent,
        canActivate: [PurchaseRequisitionActiveGuard],
    },
    {
        path: 'demand/:demandId/post-announce-task',
        canActivate: [PurchaseRequisitionActiveGuard],
        component: PostAnnouncePurchaseRequisitionTaskComponent,
    },
    {
        path: 'purchase-requisition/list',
        canActivate: [PurchaseRequisitionActiveGuard],
        component: PurchaseRequisitionListComponent,
    },
    {
        path: 'purchase-requisition/:id/detail',
        canActivate: [PurchaseRequisitionActiveGuard],
        component: PurchaseRequisitionDetailComponent,
    },
];
